import { BrowserRouter, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AdminApp } from '@/AdminApp';
import { AUTH_CONFIGURED, AuthProvider, useAuthSession } from '@/auth';
import { authErrorCode, describeAuthError } from '@/auth/authErrorMessage';
import { sanitizeReturnTo } from '@/auth/returnTo';
import { AdminAccessContext } from '@/hooks/useAdminAccess';
import { useAuth } from '@/hooks/useAuth';
import { hasAnyAdminAccess } from '@/lib/platformAdmin';
import { ForbiddenPage } from '@/pages/ForbiddenPage';
import { SignInPage } from '@/pages/SignInPage';

function FullScreenSpinner({ label }: { label: string }) {
  return (
    <div className="flex h-screen w-full items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function AppShell() {
  const { isPlatformAdmin, isStudioAdmin, canBuildIntegrations, canApproveIntegrations, canCurateTemplates, canCurateSkills } = useAuth();
  return (
    <AdminAccessContext.Provider value={{ isPlatformAdmin, isStudioAdmin, canBuildIntegrations, canApproveIntegrations, canCurateTemplates, canCurateSkills }}>
      <AdminApp />
    </AdminAccessContext.Provider>
  );
}

function AuthenticatedAppShell() {
  const { isAuthenticated, isLoading, isPlatformAdmin, isStudioAdmin, canBuildIntegrations, canApproveIntegrations, canCurateTemplates, canCurateSkills, permissionResolved, logout } = useAuth();
  const { error } = useAuthSession();

  if (error) {
    // Studio copy, never the provider's sentence: an IdP can answer an
    // org-gated refusal with the user's id and the `org_…` id, neither of
    // which Studio shows anyone.
    const notice = describeAuthError(error);
    console.warn('[Studio Admin] Sign-in failed:', notice.kind, authErrorCode(error));
    return (
      <div className="flex h-screen w-full items-center justify-center bg-background">
        <div className="max-w-md px-6 text-center">
          <h1 className="mb-2 text-xl font-semibold">{notice.title}</h1>
          <p className="mb-4 text-sm text-muted-foreground">{notice.body}</p>
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground"
            >
              Retry
            </button>
            {notice.canSwitchAccount && (
              <button
                type="button"
                onClick={() => {
                  void logout({ logoutParams: { returnTo: window.location.origin } });
                }}
                className="rounded border border-border px-4 py-2 text-sm text-muted-foreground"
              >
                Use a different account
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || (isAuthenticated && !permissionResolved)) {
    return <FullScreenSpinner label="Loading" />;
  }

  if (!isAuthenticated) {
    return <SignInPage />;
  }

  if (!hasAnyAdminAccess({ isPlatformAdmin, isStudioAdmin, canBuildIntegrations, canApproveIntegrations, canCurateTemplates, canCurateSkills })) {
    return <ForbiddenPage />;
  }

  return (
    <AdminAccessContext.Provider value={{ isPlatformAdmin, isStudioAdmin, canBuildIntegrations, canApproveIntegrations, canCurateTemplates, canCurateSkills }}>
      <AdminApp />
    </AdminAccessContext.Provider>
  );
}

function AuthProviderWithNavigate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <AuthProvider
      onRedirectCallback={(returnTo) => {
        navigate(sanitizeReturnTo(returnTo), { replace: true });
      }}
    >
      {children}
    </AuthProvider>
  );
}

export default function App() {
  return (
    <>
      <Toaster position="bottom-right" />
      {!AUTH_CONFIGURED ? (
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      ) : (
        <BrowserRouter>
          <AuthProviderWithNavigate>
            <AuthenticatedAppShell />
          </AuthProviderWithNavigate>
        </BrowserRouter>
      )}
    </>
  );
}
